import React from 'react';
import { useTranslation } from 'react-i18next';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import Autocomplete from '@mui/material/Autocomplete';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import Constants from 'style/constants';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';

import styles from './multi-value-search-filter-chip.module.scss';

type Props = {
  options: FilterChipOption[];
  onOptionsSelected: (keys: string[]) => void;
  placeHolder: string;
  selectedOptions: FilterChipOption[];
};

const MultiValueSearchFilterChip = ({ options, onOptionsSelected, placeHolder, selectedOptions }: Readonly<Props>): React.JSX.Element => {
  const { t } = useTranslation('filterChip');

  const textFieldStyle = {
    '& .MuiInputLabel-root': {
      fontFamily: 'Inter',
      fontWeight: 500,
      fontSize: '14px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'no-wrap',
    },
    minWidth: '250px',
    width: 'fit-content',
    maxWidth: '480px',
    '&:hover': {
      background: '#F5F1F5',
      borderRadius: '8px',
    },
    '& label.Mui-focused': {
      color: 'black',
    },
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      transform: 'translate(16px, 5.5px) scale(1)',
      color: Constants.Colors.onBackground,
      lineHeight: '1.5',
    },
    '& .MuiOutlinedInput-root': {
      minHeight: 32,
      borderRadius: '8px',
      padding: '4px 32px 4px 8px !important',
      alignItems: 'center',
    },
    '& .MuiAutocomplete-input': {
      height: 16,
      padding: '4px 8px !important',
      boxSizing: 'border-box',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${Constants.Colors.outline}`,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${Constants.Colors.outline}`,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      border: `1px solid ${Constants.Colors.outline}`,
    },
    '& .MuiSvgIcon-root': {
      color: Constants.Colors.onBackground,
    },
  };

  const chipStyle = {
    border: 'none',
    height: 16,
    '& .MuiChip-label': {
      fontSize: '12px',
    },
    '& .MuiChip-deleteIcon': {
      color: Constants.Colors.outline,
    },
  };

  const paperStyle = {
    backgroundColor: Constants.detailsPanelBody,
    '& .MuiAutocomplete-option': {
      borderRadius: '24px',
      margin: '4px 8px',
    },
    '& .MuiAutocomplete-option[aria-selected]': {
      background: 'transparent',
    },
    '& .MuiAutocomplete-option[aria-selected]:hover': {
      backgroundColor: '#EDEDED',
    },
  };

  const checkBoxStyle = {
    backgroundColor: Constants.surface1,
    '&:hover': {
      background: 'transparent',
    },
  };

  const autoCompleteStyle = {
    popupIndicator: {
      sx: {
        borderRadius: '8px',
      },
    },
    chip: {
      sx: chipStyle,
    },
    paper: {
      sx: paperStyle,
    },
  };

  return (
    <Autocomplete
      multiple
      id="multi-value-search-filter-chip"
      options={options}
      value={selectedOptions}
      size="small"
      limitTags={1}
      disableCloseOnSelect
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      noOptionsText={t('noFilterOptions')}
      onChange={(event, newValue) => {
        const newSelectedValues = newValue.flatMap((option) => option.value);
        onOptionsSelected(newSelectedValues);
      }}
      renderInput={(params) => (
        <TextField {...params} className={selectedOptions.length > 0 ? styles.active : ''} label={placeHolder} sx={textFieldStyle} />
      )}
      renderOption={(liProps, option, { selected }) => (
        <li {...liProps}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            size="small"
            checked={selected}
            sx={checkBoxStyle}
          />
          {option.label}
        </li>
      )}
      slotProps={autoCompleteStyle}
    />
  );
};

export default MultiValueSearchFilterChip;
